import React, { useState, useEffect, useRef } from "react";
import { useSelector, useDispatch } from "react-redux";
import { apiErrorsHandler, getErrorMessages, api } from "../api/httpclient";
import { addNotification, removeNotification } from "../redux/slices/notificationsslice";
import { globalGenerateReport, globalCancelReport, setProcessing } from "../redux/slices/reportingviewpageslice";
import { Notification, NotificationGroup } from "@progress/kendo-react-notification";
import { notificationDurationShort, notificationDurationMiddle, notificationDurationLong } from "../lib/utils";
import { Fade, Slide, Push, Reveal } from "@progress/kendo-react-animation";
import { beep, beepFile, isNumeric, compareUnary } from "../lib/utils";
import { getTilesForGenerateGlobalReport, WIDGET_GAUGE_CARD, WIDGET_NUMERIC_CARD } from "./widgets/utils";
import i18next from "i18next";
import { useTranslation } from "react-i18next";

export const NotificationsManager = props =>{
    const dispatch = useDispatch();
    const notificationsPageState = useSelector(state => state.notificationsPage);
    const notifications = useSelector(state => state.notifications);
    const notificationsClone = useRef([]);
    const [reRender, setReRender] = useState(true);
    const reportingViewPageState = useSelector(state => state.reportingViewPage);
    const currentTheme = useSelector(state => state.currentTheme);
    const currentUser = useSelector(state => state.currentUser);    
    const _currentUser = useRef(null);
    _currentUser.current = currentUser;
    const _reportingViewPageState = useRef(null);
    _reportingViewPageState.current = reportingViewPageState;
    const { t } = useTranslation();

    //const signalR = useSelector(state => state.signalR);

    const position = {
        topLeft: {
          top: 0,
          left: 0,
          alignItems: "flex-start",
        },
        topCenter: {
          top: 0,
          left: "50%",
          transform: "translateX(-50%)",
        },
        topRight: {
          top: 0,
          right: 0,
          alignItems: "flex-end",
        },
        bottomLeft: {
          bottom: 0,
          left: 0,
          alignItems: "flex-start",
        },
        bottomCenter: {
          bottom: 0,
          left: "50%",
          transform: "translateX(-50%)",
        },
        bottomRight: {
          bottom: 0,
          right: 0,
          alignItems: "flex-end",
        },
      };

    useEffect(() =>{
        let reRender = false;

        //get rid from rendered >=2 && not visible
        notificationsClone.current = notificationsClone.current.filter(e => e.visible == true || e.rendered < 2);

        //get all entries in notifications and not in clone and add them to clone with visible=true
        const toAdd = notifications.filter(e => !notificationsClone.current.some(c => c.id == e.id));
        if (toAdd?.length > 0)
        {
            for (let notification of toAdd)
                 notificationsClone.current = [...notificationsClone.current, {...notification, visible: true, rendered: 1}];            

            reRender = true;
        }

        const toRemove = notificationsClone.current.filter(e => e.visible == true && !notifications.some(c => c.id == e.id));
        if (toRemove?.length > 0)
        {
            notificationsClone.current = notificationsClone.current.map(e => {
                if (toRemove.some(r => r.id == e.id))
                    return({...e, visible: false, rendered: e.rendered + 1});
                else
                    return e;
            });

            reRender = true;
        }

        if (reRender)
            setReRender(prevState => !prevState);
    });

    
    const generateLiveReport = () => {
        let reportingViewPageState = _reportingViewPageState.current;

        if (!reportingViewPageState.pageLoaded || reportingViewPageState.pageDisposed || !reportingViewPageState.isLiveReport)
            return;

        if (!reportingViewPageState.globalFilter || !reportingViewPageState.globalFilter.isVisible || reportingViewPageState.globalGenerate)// reportingViewPageState.globalRequestId)
           return;

        if (reportingViewPageState.tiles.find(e => e.requestId != null))
           return;

        const newRequestId = Math.ceil((Math.random()*100000000 + 1)).toString() + (Date.now().toString());// (new Date()).toString();
        //console.log("REQUESTID", newRequestId);

        let tiles = getTilesForGenerateGlobalReport(reportingViewPageState, newRequestId);

        dispatch(globalGenerateReport(tiles)).then(
            response => {
                //dispatch(setPageCommands(commands));
            },
            err =>{
                //hide loader
                dispatch(setProcessing({processing: false}));
                dispatch(globalCancelReport());


                //do not show notification if failed because timeout and global request id is not newRequestId
                if(err && err.succeeded == false && err.timedOut && reportingViewPageState.globalRequestId != newRequestId)
                   return;

                const handleResponseReceived = (state, dispatch, navigate, payload) =>{
                    let errorMessages = payload?.data?.ErrorMessages;

                    //notify with error
                    if (!errorMessages || errorMessages.length == 0)
                        dispatch(addNotification({text: t("notifmanager.loading_errormessage"), type: "error", closable: true, duration:notificationDurationMiddle, position:"topRight"}));
                    else
                    {
                        errorMessages = getErrorMessages(errorMessages, 1);
                        dispatch(addNotification({text: errorMessages[0], type: "error", closable: true, duration:notificationDurationMiddle, position:"topRight"}));
                    }
                };

                const handleUnhandled = (state, dispatch, navigate, payload) =>{
                    dispatch(addNotification({text: t("notifmanager.unknown_error"), type:"error", closable: true, duration: notificationDurationMiddle, position: "topRight"}));
                };

                apiErrorsHandler({}, dispatch, null, err, null, {responseReceived: handleResponseReceived, unhandled: handleUnhandled});
            }
        );
    }

    return (
        <React.Fragment>
            <NotificationGroup style={position.bottomRight}>
            {
                notificationsClone.current.filter(notification => notification.position=="bottomRight").map((notification, index) => {
                    return (
                        <Reveal transitionExitDuration={1000} key={notification.id}>                        
                        {
                            notification.visible && 
                            <Notification
                                type={{
                                    style: `${notification.type}`,
                                    icon: true,
                                }}
                                closable={notification.closable}
                                onClose={() => dispatch(removeNotification(notification.id))}
                                >
                                    {!notification.isHtml && <span>{notification.text}</span>}
                                    {notification.isHtml && <div>{notification.text}</div>}
                            </Notification>           
                        }             
                        </Reveal>
                    );
                })
            }        
            </NotificationGroup>

            <NotificationGroup style={position.bottomCenter}>
            {
                notificationsClone.current.filter(notification => notification.position=="bottomCenter").map((notification, index) => {
                    return (
                        <Reveal   transitionExitDuration={1000} key={notification.id}>                        
                        {
                            notification.visible && <Notification
                                type={{
                                    style: `${notification.type}`,
                                    icon: true,
                                }}
                                closable={notification.closable}
                                onClose={() => dispatch(removeNotification(notification.id))}
                                >
                                    {!notification.isHtml && <span>{notification.text}</span>}
                                    {notification.isHtml && <div>{notification.text}</div>}
                            </Notification>           
                        }             
                        </Reveal>
                    );
                })
            }        
            </NotificationGroup>

            <NotificationGroup style={position.topRight}>
            {
                notificationsClone.current.filter(notification => notification.position=="topRight").map((notification, index) => {
                    return (
                        <Reveal transitionExitDuration={1000} key={notification.id}>                        
                        {
                            notification.visible && <Notification
                                type={{
                                    style: `${notification.type}`,
                                    icon: true,
                                }}
                                closable={notification.closable}
                                onClose={() => dispatch(removeNotification(notification.id))}
                                >
                                    {!notification.isHtml && <span>{notification.text}</span>}
                                    {notification.isHtml && <div>{notification.text}</div>}
                            </Notification>           
                        }             
                        </Reveal>
                    );
                })
            }        
            </NotificationGroup>

            <NotificationGroup style={position.topCenter}>
            {
                notificationsClone.current.filter(notification => notification.position=="topCenter").map((notification, index) => {
                    return (
                        <Reveal transitionExitDuration={1000} key={notification.id}>                        
                        {
                            notification.visible && <Notification
                                type={{
                                    style: `${notification.type}`,
                                    icon: true,
                                }}
                                closable={notification.closable}
                                onClose={() => dispatch(removeNotification(notification.id))}
                                >
                                    {!notification.isHtml && <span>{notification.text}</span>}
                                    {notification.isHtml && <div>{notification.text}</div>}
                            </Notification>           
                        }             
                        </Reveal>
                    );
                })
            }        
            </NotificationGroup>
            
            <style>
            {`
                .k-notification
                {
                    padding:18px 2px;
                    font-size: 0.9rem;
                    max-width: 400px;
                }
                
                .k-notification-wrap > .k-icon{
                    margin: 2px 18px 0 18px;
                }
                
                .k-notification-container
                {
                    margin:2px 2px;
                }

                .k-notification-error
                {
                    background-color: ${currentTheme.errorFaded};
                    color: ${currentTheme.error};
                    border-color: ${currentTheme.error};
                }

                .k-notification-success
                {
                    background-color: ${currentTheme.successFaded};
                    color: ${currentTheme.success};
                    border-color: ${currentTheme.success};
                }

                .k-notification-warning
                {
                    background-color: ${currentTheme.warningFaded};
                    color: ${currentTheme.warning};
                    border-color: ${currentTheme.warning};
                }

                .k-notification-info
                {
                    background-color: ${currentTheme.infoFaded};
                    color: ${currentTheme.info};
                    border-color: ${currentTheme.info};
                }
            `}
            </style>
        </React.Fragment>
    );
};